import { useState } from 'react'

import { GAMES, toReadingBand, type ReadingBand } from '@hazel/shared'

import { Button } from '../components/Button.js'
import { useBarTitle } from '../layouts/bar-title.js'
import { letterFrom, useGameKeys } from './useGameKeys.js'
import { Card } from '../components/Card.js'
import { Announce } from './Announce.js'
import { useRoundFocus } from './useRoundFocus.js'
import { useLeaveOnEscape } from './useLeaveOnEscape.js'
import { LeaveGame } from './LeaveGame.js'
import { KeyLegend } from '../components/KeyLegend.js'
import { LEAVE_SHORTCUT } from '../components/shortcuts.js'
import { Milestone } from './Milestone.js'
import { PlayAgain } from './PlayAgain.js'
import { RaceTrack } from './RaceTrack.js'
import { LANE, RACE_ART, racerPicture } from './raceArt.js'
import { useScramble } from './useScramble.js'
import { useRound, type DealtRound } from './useRound.js'
import { useCueWhen, useRoundEndCues, useRoundMusic } from './gameSound.js'
import { TRACK_FOR_GAME } from '../sound/cues.js'
import { useSound } from '../sound/sound-context.js'

/**
 * Small Rabbit's Scramble.
 *
 * Tiles rather than a text box. A child who can see the letters should be able
 * to move them, and a phone keyboard covering half the puzzle while she hunts
 * for a Y is the opposite of that.
 */
export function Scramble() {
  // The bar carries the name while she is here, in place of "Hazel's
  // Games". One call rather than one per screen: the chooser and the
  // board are both this component, so the name does not blink out between
  // picking a size and being dealt one.
  useBarTitle(GAMES.scramble.title)

  /*
    The screen's music, not the round's.

    On the round it started when play did — so Sky's was silent through "How
    big a board?", and every game had a beat of nothing while its round was
    being dealt. Asking here means it also survives another go, which is the
    same screen and should not restart the track.
  */
  useRoundMusic(TRACK_FOR_GAME.scramble)

  const { round, again, failed } = useRound<{ band: ReadingBand }>({
    game: 'scramble',
    band: toReadingBand(null),
  })

  if (failed) {
    return (
      <p role="alert" className="text-coral py-16 text-center">
        We couldn’t start a puzzle just now. Have another go in a moment.
      </p>
    )
  }

  // Nothing is dealt until the server has said what to deal.
  if (!round) {
    return (
      <p role="status" className="py-16 text-center">
        Getting a word ready…
      </p>
    )
  }

  // Keyed on the seed, so another word is a fresh puzzle rather than the old
  // one relabelled.
  return <ScrambleRound key={round.seed} round={round} onAgain={again} />
}

function ScrambleRound({
  round,
  onAgain,
}: {
  round: DealtRound<{ band: ReadingBand }>
  onAgain: () => void
}) {
  const leaveOnEscape = useLeaveOnEscape()
  const game = useScramble(round, onAgain)
  const { view } = game
  const board = useRoundFocus<HTMLDivElement>(round.seed, view.status !== 'playing')

  const letters = view.letters
  const arrangement = letters.join('')

  /**
   * Which tiles she has used, in the order she tapped them.
   *
   * Stored against the arrangement they belong to, so mixing the letters up or
   * moving to a new word drops them without an effect having to notice and
   * clear them afterwards — which would render once with positions pointing at
   * tiles that had already moved.
   */
  const [picked, setPicked] = useState({ arrangement, taken: [] as number[] })

  const taken = picked.arrangement === arrangement ? picked.taken : []
  const built = taken.map((index) => letters[index] ?? '').join('')

  const sound = useSound()

  /*
    Cued at the gesture, not inferred from the tally.

    Watching `taken.length` rise and fall looked right and was not: `taken` is
    derived — `picked.arrangement === arrangement ? picked.taken : []` — so it
    collapses to nothing whenever the letters change underneath it, without
    anybody having put a tile back. Both mixing them up and solving a word do
    exactly that, so a shuffle sounded like a shuffle *and* a tile going back,
    and every word she got right was followed by a tile going back that never
    went anywhere.

    Naming the gestures is the fix: the only things that make these sounds are
    the things she actually does.
  */
  const takeTile = (index: number) => {
    sound.play('tile.take')
    setTaken([...taken, index])
  }

  const putTilesBack = (next: number[]) => {
    sound.play('tile.back')
    setTaken(next)
  }

  const mixThemUp = () => {
    sound.play('shuffle')
    game.shuffle()
  }

  useCueWhen(view.hintShown, 'clue')
  useRoundEndCues(view.status !== 'playing', game.milestone)

  const setTaken = (next: number[]) => setPicked({ arrangement, taken: next })

  function submit(): void {
    if (built === '') return
    game.answer(built)
    setTaken([])
  }

  /**
   * Typing takes tiles.
   *
   * Closer to how anybody would actually try it than tabbing between eight
   * buttons — and on a television it is the only way. A letter takes the
   * leftmost tile still showing it, so typing the word out picks the tiles in
   * the order you would have tapped them.
   */
  useGameKeys((event) => {
    // Before the status gate: a solved word is exactly when somebody wants
    // out, and there are no letters left to give back once it is solved.
    if (event.key === 'Escape' && view.status !== 'playing') {
      return void leaveOnEscape(false)
    }

    if (view.status !== 'playing') {
      if (event.key === 'Enter') game.again()
      return
    }

    const letter = letterFrom(event)
    if (letter) {
      const tile = letters.findIndex(
        (each, index) => each.toLowerCase() === letter && !taken.includes(index),
      )
      if (tile !== -1) takeTile(tile)
      return
    }

    if (event.key === 'Enter') return submit()
    if (event.key === 'Backspace') return putTilesBack(taken.slice(0, -1))
    if (event.key === 'Escape') {
      if (leaveOnEscape(taken.length > 0)) return
      return putTilesBack([])
    }
    // A fresh arrangement, for somebody who cannot reach the button.
    if (event.key === ' ') {
      event.preventDefault()
      mixThemUp()
    }

    /*
     * A clue, for somebody who cannot reach the button either.
     *
     * Both halves of the same physical key: "?" is the one that means
     * something, and "/" is what you get if you do not reach for shift. On a
     * sofa with a Bluetooth keyboard, holding one key while pressing another
     * is a genuinely harder thing to ask than pressing one.
     *
     * Always prevented, whichever it was: "/" opens quick-find in Firefox, so
     * asking for a clue would also open a search bar over the game and swallow
     * every letter she typed next.
     */
    if (event.key === '?' || event.key === '/') {
      event.preventDefault()
      if (!view.hintShown) game.askForHint()
    }
  })

  return (
    <div className="mx-auto flex w-full max-w-xl [[data-scale=tv]_&]:max-w-none [[data-scale=tv]_&]:min-h-0 [[data-scale=tv]_&]:flex-1 flex-col items-center gap-4">
      <Card
        ref={board}
        tabIndex={-1}
        // On the television the card takes the height that is left and spreads
        // its rows through it, rather than bunching them under the race and
        // leaving a hand's width of empty screen along the bottom.
        className="outline-none flex w-full flex-col items-center gap-4 [[data-scale=tv]_&]:min-h-0 [[data-scale=tv]_&]:flex-1 [[data-scale=tv]_&]:justify-evenly"
      >
        <Announce>
          {[
            view.status !== 'playing'
              ? `${view.answer?.toUpperCase() ?? ''} — you got it!`
              : view.lastGuess && !view.lastGuess.correct
                ? `${view.lastGuess.guess.toUpperCase()} isn’t it — have another go.`
                : '',
            built === '' ? 'Nothing spelled out yet' : `You have: ${built}`,
            view.hint ? `Clue: ${view.hint}` : '',
            game.milestone ? `That’s ${game.milestone} games played.` : '',
          ]
            .filter(Boolean)
            .join('. ')}
        </Announce>

        {/*
          Small Rabbit, carrying the letters home.

          Deliberately not a race. Letters are only judged when she answers, so
          steps for taking a tile would be steps for fiddling rather than for
          getting it right, and an opponent gaining on her for thinking would
          be inventing jeopardy that is not in this game. A step per letter
          carried, a step back when one is returned, and the burrow reached
          when the word is assembled — a filling-up, not a win. Three games
          that race and one that does not is a feature; four would be
          relentless.
        */}
        <RaceTrack
          goal={RACE_ART.burrow}
          racers={[
            {
              key: 'small-rabbit',
              name: 'Small Rabbit',
              picture: racerPicture('small-rabbit'),
              /*
               * Home once she has got it, rather than back at the start.
               *
               * Answering puts every tile back — that is the game's behaviour
               * and the race follows it — but a correct answer *ends* the
               * round, so the same reset walked her home and then straight
               * back while the finished word was still on screen. The round is
               * over and she finished it; the race saying otherwise is the
               * same fault as a finished round moving somebody who did not
               * earn it, which Hoppity's had once already.
               *
               * A scramble only ever ends by being solved — there is no losing
               * status — so a finished round is always one she won.
               */
              steps: view.status === 'playing' ? taken.length : letters.length,
              of: letters.length,
              tint: LANE.rabbit,
            },
          ]}
        />

        <div className="flex flex-col items-center gap-3">
          {view.lastGuess && !view.lastGuess.correct ? (
            <p className="text-ink-muted">
              {`${view.lastGuess.guess.toUpperCase()} isn’t it — have another go.`}
            </p>
          ) : null}

          <p
            className="font-display border-border min-h-16 min-w-48 rounded-(--radius-control) border-2 px-4 py-2 text-(length:--text-title) tracking-widest uppercase"
            aria-label={built === '' ? 'Nothing spelled out yet' : `You have: ${built}`}
          >
            {built || '\u00a0'}
          </p>
        </div>

        {view.status === 'playing' ? (
          <>
            <ul className="flex list-none flex-wrap justify-center gap-2 p-0">
              {letters.map((letter, index) => {
                const used = taken.includes(index)

                return (
                  <li key={index}>
                    <button
                      type="button"
                      // Position, not just the letter: a word with two Es has
                      // two identical tiles, and "E" twice tells a screen
                      // reader nothing about which one moved.
                      aria-label={`${letter.toUpperCase()}, letter ${index + 1}${
                        used ? ', used — tap to take it back' : ''
                      }`}
                      aria-pressed={used}
                      style={{ touchAction: 'manipulation' }}
                      // Tapping a used tile takes that letter back out, rather
                      // than the tile going dead. Putting a letter in and
                      // taking it out again should be the same gesture — a
                      // child who has just tapped the wrong E should be able to
                      // undo it by tapping the wrong E.
                      onClick={() =>
                        used
                          ? putTilesBack(taken.filter((each) => each !== index))
                          : takeTile(index)
                      }
                      className={[
                        'font-display min-h-14 min-w-14 rounded-(--radius-control) border-2 px-3',
                        // A tile on the television held a 64px letter in 63px
                        // of tile. The tap token is what a comfortable target
                        // is at each scale, so the tile is at least that.
                        '[[data-scale=tv]_&]:min-h-(--size-tap) [[data-scale=tv]_&]:min-w-(--size-tap)',
                        'text-(length:--text-title) uppercase transition-colors',
                        used
                          ? 'border-ink-muted bg-surface-sunken text-ink-muted'
                          : 'border-border bg-surface hover:border-ink-muted',
                      ].join(' ')}
                    >
                      {letter}
                    </button>
                  </li>
                )
              })}
            </ul>

            {/*
              Gone on the television, where there is no way to press them.
              
              Every one of these four is on the key legend along the bottom —
              Enter, Bksp, Space and the question mark — so on a set with no
              mouse they are a row of the screen spent saying a second time
              what the legend already says. Everywhere else they are how the
              game is played with a finger, and they stay.
            */}
            <div className="flex flex-wrap justify-center gap-2 [[data-scale=tv]_&]:hidden">
              <Button onClick={submit} disabled={built === ''}>
                That’s it!
              </Button>
              <Button
                variant="secondary"
                // Kept alongside tapping a tile, because a tile you cannot
                // remember tapping is hard to find again — this always removes
                // the last one, whichever it was.
                onClick={() => putTilesBack(taken.slice(0, -1))}
                disabled={taken.length === 0}
              >
                Take one back
              </Button>
              <Button variant="secondary" onClick={mixThemUp}>
                Mix them up
              </Button>
              {!view.hintShown ? (
                <Button variant="secondary" onClick={game.askForHint}>
                  Give me a clue
                </Button>
              ) : null}
            </div>

            {/* On request, not by default. A clue nobody asked for takes the
                puzzle away. */}
            {view.hint ? (
              <p className="text-ink-muted [[data-scale=tv]_&]:text-(length:--text-heading)">
                Clue: {view.hint}
              </p>
            ) : null}
          </>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <p className="font-display text-(length:--text-title)">
              {`${view.answer?.toUpperCase() ?? ''} — you got it!`}
            </p>
            <p className="text-ink-muted [[data-scale=tv]_&]:text-(length:--text-heading)">
              Clue: {view.hint}
            </p>
            {game.milestone ? <Milestone games={game.milestone} /> : null}

            <PlayAgain label="Another word" does="to get another word" onAgain={game.again} />
          </div>
        )}
      </Card>

      <LeaveGame />

      <KeyLegend
        shortcuts={
          view.status === 'playing'
            ? [
                { keys: ['A', '–', 'Z'], does: 'Take that letter' },
                { keys: ['Enter'], does: 'Answer' },
                { keys: ['Bksp'], does: 'Take one back' },
                { keys: ['Space'], does: 'Mix them up' },
                /*
                 * Offered only while there is a clue left to give.
                 *
                 * The button was already conditional and the legend was not,
                 * so once the hint was up the legend went on advertising a key
                 * that does nothing. An instruction on screen that is not true
                 * is worse than none, and this app has been caught by exactly
                 * that before — Hoppity's legend went on offering Enter to
                 * guess the whole word after the box had gone.
                 *
                 * It matters more on the television, where the button is
                 * hidden and the legend is the only thing that says anything
                 * about this key at all.
                 *
                 * "/" does the same and is the same key unshifted, but two
                 * keycaps side by side read as "press both".
                 */
                ...(view.hintShown ? [] : [{ keys: ['?'], does: 'Give me a clue' }]),
                LEAVE_SHORTCUT,
              ]
            : [{ keys: ['Enter'], does: 'Another word' }, LEAVE_SHORTCUT]
        }
      />
    </div>
  )
}
